import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import {
  decimalSolicitud,
  entradaDecimal,
  type ConfiguracionLiquidacion,
  type ParametrosSexo,
} from '@/lib/liquidacion';
import {
  useConfiguracionLiquidacion,
  useGuardarConfiguracionLiquidacion,
} from '@/lib/queries/liquidacion';
import { useAuthStore } from '@/stores/auth';
import type { Sexo } from '@/lib/types';

type TramoForm = { edad_min: string; edad_max: string; porcentaje: string };
type SexoForm = Omit<ParametrosSexo, 'tramos'> & { tramos: TramoForm[] };
type ConfigForm = Omit<ConfiguracionLiquidacion, 'sexos'> & { sexos: Record<Sexo, SexoForm> };
const coeficientes = ['base', 'complemento', 'coef_dias', 'coef_conversion', 'coef_bajas'] as const;
const generales = [
  'porcentaje_iva',
  'ajuste_carga_predeterminado_eur',
  'bonificacion_predeterminada_eur',
] as const;
const inputStyle =
  'min-h-11 w-full min-w-0 rounded-lg border border-border bg-surface px-3 py-2 text-sm tabular-nums focus-visible:outline-accent';

export default function AjustesLiquidacionPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  if (user?.rol === 'operario')
    return (
      <main className="mx-auto max-w-md px-4">
        <p role="alert">{t('liquidacion.sin_permiso')}</p>
      </main>
    );
  return <ConfiguracionContenido />;
}
function ConfiguracionContenido() {
  const { t } = useTranslation();
  const q = useConfiguracionLiquidacion();
  return (
    <main className="mx-auto max-w-md px-4 pb-8">
      <Link
        to="/ajustes"
        className="mb-3 inline-flex min-h-11 items-center text-sm text-accent hover:underline"
      >
        {t('liquidacion.volver_ajustes')}
      </Link>
      <h1 className="text-xl font-bold">{t('liquidacion.ajustes')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('liquidacion.ajustes_descripcion')}</p>
      {q.isLoading && <p className="mt-5">{t('common.loading')}</p>}
      {q.isError && (
        <p role="alert" className="mt-5 text-warning-text">
          {extractApiMessage(q.error)}
        </p>
      )}
      {q.data && <ConfiguracionForm inicial={q.data} />}
    </main>
  );
}
function ConfiguracionForm({ inicial }: { inicial: ConfiguracionLiquidacion }) {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const editable = user?.rol === 'admin' || user?.rol === 'ganadero';
  const guardar = useGuardarConfiguracionLiquidacion();
  const [sexo, setSexo] = useState<Sexo>('machos');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ConfigForm>(() => ({
    ...inicial,
    ...Object.fromEntries(generales.map((k) => [k, entradaDecimal(inicial[k])])),
    sexos: Object.fromEntries(
      (['machos', 'hembras'] as const).map((s) => [
        s,
        {
          ...inicial.sexos[s],
          ...Object.fromEntries(coeficientes.map((k) => [k, entradaDecimal(inicial.sexos[s][k])])),
          tramos: inicial.sexos[s].tramos.map((tramo) => ({
            edad_min: String(tramo.edad_min),
            edad_max: tramo.edad_max === null ? '' : String(tramo.edad_max),
            porcentaje: entradaDecimal(tramo.porcentaje),
          })),
        },
      ]),
    ) as Record<Sexo, SexoForm>,
  }));
  function cambiarTramo(i: number, key: keyof TramoForm, value: string) {
    setError(null);
    setForm((actual) => ({
      ...actual,
      sexos: {
        ...actual.sexos,
        [sexo]: {
          ...actual.sexos[sexo],
          tramos: actual.sexos[sexo].tramos.map((tramo, j) =>
            j === i ? { ...tramo, [key]: value } : tramo,
          ),
        },
      },
    }));
  }
  async function guardarParametros() {
    setError(null);
    try {
      const body: ConfiguracionLiquidacion = { ...inicial, sexos: { ...inicial.sexos } };
      for (const key of generales) {
        const value = decimalSolicitud(form[key]);
        if (!value || !Number.isFinite(Number(value))) throw new Error(t('liquidacion.numero'));
        body[key] = value;
      }
      for (const s of ['machos', 'hembras'] as const) {
        const p = form.sexos[s];
        const tramos = p.tramos.map((tramo) => {
          if (
            !/^\d+$/.test(tramo.edad_min) ||
            (tramo.edad_max !== '' && !/^\d+$/.test(tramo.edad_max))
          )
            throw new Error(t('liquidacion.error_tramos'));
          const percentage = decimalSolicitud(tramo.porcentaje);
          if (
            !percentage ||
            !Number.isFinite(Number(percentage)) ||
            Number(percentage) < 0 ||
            Number(percentage) > 100
          )
            throw new Error(t('liquidacion.error_porcentaje'));
          return {
            edad_min: Number(tramo.edad_min),
            edad_max: tramo.edad_max === '' ? null : Number(tramo.edad_max),
            porcentaje: percentage,
          };
        });
        if (
          !tramos.length ||
          tramos[0].edad_min !== 0 ||
          tramos.at(-1)?.edad_max !== null ||
          tramos.some(
            (tramo, i) =>
              (tramo.edad_max !== null && tramo.edad_max <= tramo.edad_min) ||
              (i > 0 && tramos[i - 1].edad_max !== tramo.edad_min),
          )
        )
          throw new Error(t('liquidacion.error_tramos'));
        body.sexos[s] = { ...inicial.sexos[s], tramos };
        for (const key of coeficientes) {
          const value = decimalSolicitud(p[key]);
          if (!value || !Number.isFinite(Number(value))) throw new Error(t('liquidacion.numero'));
          body.sexos[s][key] = value;
        }
      }
      await guardar.mutateAsync(body);
      toast.show({ kind: 'success', message: t('liquidacion.parametros_guardados') });
    } catch (err) {
      setError(extractApiMessage(err));
    }
  }
  return (
    <div className="mt-5 space-y-5">
      <div className="grid grid-cols-2 gap-2">
        {(['machos', 'hembras'] as const).map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={sexo === s}
            onClick={() => setSexo(s)}
            className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-medium ${sexo === s ? 'border-accent bg-accent text-accent-contrast' : 'border-border bg-surface'}`}
          >
            {t(`pienso_ajustes.${s}`)}
          </button>
        ))}
      </div>
      {!editable && <p className="text-sm text-text-secondary">{t('liquidacion.solo_lectura')}</p>}
      <fieldset disabled={!editable || guardar.isPending} className="space-y-4">
        <legend className="mb-3 font-semibold">{t('liquidacion.coeficientes')}</legend>
        <div className="grid grid-cols-2 gap-4">
          {coeficientes.map((key) => (
            <label key={key} className="text-sm font-medium">
              {t(`liquidacion.${key}`)}
              <input
                className={`${inputStyle} mt-1`}
                inputMode="decimal"
                value={form.sexos[sexo][key]}
                onChange={(e) => {
                  setError(null);
                  setForm((actual) => ({
                    ...actual,
                    sexos: {
                      ...actual.sexos,
                      [sexo]: { ...actual.sexos[sexo], [key]: e.target.value },
                    },
                  }));
                }}
              />
            </label>
          ))}
        </div>
        <h2 className="pt-2 font-semibold">{t('liquidacion.tramos')}</h2>
        <p className="text-xs text-text-secondary">{t('liquidacion.tramos_ayuda')}</p>
        {form.sexos[sexo].tramos.map((tramo, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2">
            {(['edad_min', 'edad_max', 'porcentaje'] as const).map((key) => (
              <label key={key} className="min-w-0 text-xs text-text-secondary">
                {t(`liquidacion.${key}`)}
                <input
                  className={`${inputStyle} mt-1 px-2`}
                  aria-label={`${t(`liquidacion.${key}`)} ${i + 1}`}
                  inputMode={key === 'porcentaje' ? 'decimal' : 'numeric'}
                  placeholder={key === 'edad_max' ? t('liquidacion.sin_limite') : undefined}
                  value={tramo[key]}
                  onChange={(e) => cambiarTramo(i, key, e.target.value)}
                />
              </label>
            ))}
            <button
              type="button"
              aria-label={t('liquidacion.eliminar_tramo', { n: i + 1 })}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-border text-danger"
              onClick={() =>
                setForm((actual) => ({
                  ...actual,
                  sexos: {
                    ...actual.sexos,
                    [sexo]: {
                      ...actual.sexos[sexo],
                      tramos: actual.sexos[sexo].tramos.filter((_, j) => i !== j),
                    },
                  },
                }))
              }
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 text-sm"
          onClick={() =>
            setForm((actual) => {
              const tramos = actual.sexos[sexo].tramos.map((tramo) => ({ ...tramo }));
              const last = tramos.at(-1);
              const min = last ? Number(last.edad_max || last.edad_min) + 10 : 0;
              if (last) last.edad_max = String(min);
              tramos.push({
                edad_min: String(min),
                edad_max: '',
                porcentaje: last?.porcentaje ?? '0',
              });
              return {
                ...actual,
                sexos: { ...actual.sexos, [sexo]: { ...actual.sexos[sexo], tramos } },
              };
            })
          }
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t('liquidacion.anadir_tramo')}
        </button>
        <div className="space-y-4 border-t border-border pt-4">
          <h2 className="font-semibold">{t('liquidacion.generales')}</h2>
          {generales.map((key) => (
            <label className="block text-sm font-medium" key={key}>
              {t(`liquidacion.${key}`)}
              <input
                className={`${inputStyle} mt-1`}
                inputMode="decimal"
                value={form[key]}
                onChange={(e) => {
                  setError(null);
                  setForm((actual) => ({ ...actual, [key]: e.target.value }));
                }}
              />
            </label>
          ))}
        </div>
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-warning-text">
          {error}
        </p>
      )}
      {editable && (
        <button
          type="button"
          disabled={guardar.isPending}
          onClick={() => void guardarParametros()}
          className="min-h-11 w-full rounded-lg bg-accent px-4 py-2 font-semibold text-accent-contrast disabled:opacity-60"
        >
          {t(guardar.isPending ? 'liquidacion.guardando' : 'liquidacion.guardar_parametros')}
        </button>
      )}
    </div>
  );
}
